import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '16/9',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#000',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#eeeeee'}`,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
}));

const VideoOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 1,
});

const PlayButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  backgroundColor: 'rgba(255,255,255,0.2)',
  color: 'white',
  width: 80,
  height: 80,
  backdropFilter: 'blur(4px)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.3)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease',
}));

const RegularVideo = ({ videoUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasClickedPlay, setHasClickedPlay] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (hasClickedPlay) return;
    setIsHovering(true);
    
    const video = videoRef.current;
    if (video) {
      video.play().catch(console.error);
    }
  }, [hasClickedPlay]);

  const handleMouseLeave = useCallback(() => {
    if (hasClickedPlay) return;
    setIsHovering(false);
    
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, [hasClickedPlay]);

  const handlePlayButtonClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!hasClickedPlay) {
      setHasClickedPlay(true);
      setIsHovering(false);
      video.currentTime = 0;
      
      setTimeout(() => {
        video.play().catch(console.error);
      }, 50);
    } else {
      if (video.paused) {
        video.play().catch(console.error);
      } else {
        video.pause();
      }
    }
  }, [hasClickedPlay]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <VideoContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={!hasClickedPlay ? handlePlayButtonClick : undefined}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        loop={isHovering && !hasClickedPlay}
        muted={!hasClickedPlay}
        controls={hasClickedPlay}
        preload="metadata"
        playsInline
        onPlay={handlePlay}
        onPause={handlePause}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#000',
          transition: 'transform 0.3s ease'
        }}
      />
      
      {/* Overlay для звичайного відео */}
      {!isHovering && !hasClickedPlay && <VideoOverlay />}
      
      {/* Кнопка Play для звичайного відео */}
      {!isPlaying && !isHovering ? (
        <PlayButton 
          size="large"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePlayButtonClick();
          }}
        >
          <PlayArrow sx={{ fontSize: 40, ml: 0.5 }} />
        </PlayButton>
      ) : null}
    </VideoContainer>
  );
};

// Компонент для YouTube відео
const YouTubeVideo = ({ videoUrl }) => {
  const [showVideo, setShowVideo] = useState(false);

  // Отримуємо YouTube ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  const youtubeId = getYouTubeId(videoUrl);
  const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;

  const handleClick = () => {
    setShowVideo(true);
  };

  return (
    <VideoContainer onClick={!showVideo ? handleClick : undefined}>
      {showVideo ? (
        // Показуємо YouTube iframe після кліка
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      ) : (
        // Показуємо thumbnail до кліка
        <>
          {thumbnailUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${thumbnailUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}
          
          <VideoOverlay />
          
          <PlayButton size="large">
            <PlayArrow sx={{ fontSize: 40, ml: 0.5 }} />
          </PlayButton>
        </>
      )}
    </VideoContainer>
  );
};

const MainVideo = ({ videoUrl }) => {
  if (!videoUrl) {
    return (
      <VideoContainer>
        <Box sx={{ color: 'white', textAlign: 'center' }}>
          No video available
        </Box>
      </VideoContainer>
    );
  }

  const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');

  return isYouTube ? (
    <YouTubeVideo videoUrl={videoUrl} />
  ) : (
    <RegularVideo videoUrl={videoUrl} />
  );
};

export default MainVideo;